import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SimulationTasksUpdates } from '@nexelem/graphne-api';
import { useCustomToast } from '@nexelem/snackbar';
import { useGraphneAPI } from '@nexelem/graphne-api';
import { useCustomTranslation } from '../../../../../hooks/useCustomTranslation';
import { taskDetailsQueryKey } from '../../../../../queryHooks/productionPlanHooks/useGraphneTaskDetails';
import { closeRecalculateModal, closeSimulationEditForm } from '../modals';
import { simulationLotsQueryKey } from './useGetGraphneSimulationLots';
import { useSimulationId } from './useSimulationId';

type UseSaveAndRecalculateParams = {
  simulationTasksUpdates: SimulationTasksUpdates;
};

export const useSaveAndRecalculate = ({ simulationTasksUpdates }: UseSaveAndRecalculateParams) => {
  const { tWrapper } = useCustomTranslation('products.simulationPlan');
  const displayToast = useCustomToast();
  const api = useGraphneAPI();
  const queryClient = useQueryClient();
  const { id } = useSimulationId();

  const saveMutation = useMutation({
    mutationFn: async () => {
      const tasks = simulationTasksUpdates.tasks ?? [];
      if (tasks.length === 0) {
        return;
      }
      await api.postGraphneSimulationSimulationidTasksUpdate({
        simulationId: id.toString(),
        simulationTasksUpdates,
      });
    },
    onSuccess: () => {
      const taskIds = simulationTasksUpdates.tasks?.map((t) => t.taskId) ?? [];
      queryClient.invalidateQueries({
        queryKey: simulationLotsQueryKey(id),
      });
      for (const taskId of taskIds) {
        queryClient.invalidateQueries({
          queryKey: taskDetailsQueryKey(taskId),
        });
      }
    },
    onError: () => {
      displayToast({
        toastType: 'error',
        message: tWrapper('saveSimulationTasksUpdateError'),
      });
    },
  });

  const handleSaveAndRecalculate = async () => {
    try {
      await saveMutation.mutateAsync();

      displayToast({
        toastType: 'success',
        message: tWrapper('recalculateModalSuccess'),
      });

      closeRecalculateModal();
      closeSimulationEditForm();
    } catch {
      closeRecalculateModal();
    }
  };

  return {
    handleSaveAndRecalculate,
    isLoading: saveMutation.isPending,
  };
};
